import { cn } from "@core-ui/utils/cn";
import { Icon } from "@ui-kit/Icon";
import { ScrollShadowContainer } from "@ui-kit/ScrollShadowContainer";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import { TREE_INDENT_STEP } from "@ui-kit/Tree";
import { forwardRef, type HTMLAttributes, type ReactNode, useId } from "react";
import { FloatingTriggerButton, type FloatingTriggerButtonProps } from "../../FloatingTriggerButton";

export type PanelListProps = HTMLAttributes<HTMLDivElement> & { children: ReactNode };

export const PanelList = ({ className, ...otherProps }: PanelListProps) => (
	<ScrollShadowContainer
		className={cn("mt-2 flex min-h-0 flex-1 flex-col overflow-x-hidden", className)}
		{...otherProps}
	/>
);

export type PanelListItemProps = Omit<HTMLAttributes<HTMLDivElement>, "title" | "children"> & {
	title: string;
	description?: string;
	selected?: boolean;
	depth?: number;
	leading?: ReactNode;
	actions?: ReactNode;
	onOpen: () => void;
	openTestId?: string;
};

export const PanelListItem = ({
	title,
	description,
	selected = false,
	depth = 0,
	leading,
	actions,
	onOpen,
	openTestId,
	className,
	style,
	...otherProps
}: PanelListItemProps) => {
	const titleId = useId();
	const descriptionId = useId();

	return (
		<div
			className={cn(
				"group/panel-item flex min-w-0 shrink-0 items-center rounded-lg text-sm transition-colors",
				"hover:bg-secondary-bg-hover data-[selected=true]:bg-primary-bg-hover data-[selected=true]:hover:bg-primary-bg-hover",
				actions && "pr-2",
				className,
			)}
			data-selected={selected}
			style={{ ...style, paddingLeft: depth * TREE_INDENT_STEP }}
			{...otherProps}
		>
			{leading && <div className="ml-2 flex size-4 shrink-0 items-center justify-center">{leading}</div>}
			<button
				aria-describedby={description ? descriptionId : undefined}
				aria-labelledby={titleId}
				className="m-0 flex min-h-8 min-w-0 flex-1 cursor-pointer appearance-none flex-col items-start justify-center rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-inherit focus-visible:shadow-focus focus-visible:outline-none"
				data-testid={openTestId}
				onClick={onOpen}
				type="button"
			>
				<span className="block w-full min-w-0 font-medium" id={titleId}>
					<TextOverflowTooltip className="block w-full">{title}</TextOverflowTooltip>
				</span>
				{description && (
					<span className="line-clamp-2 text-xs font-normal text-muted" id={descriptionId}>
						{description}
					</span>
				)}
			</button>
			{actions && <div className="flex size-6 shrink-0 items-center justify-center">{actions}</div>}
		</div>
	);
};

/** Shown while the item is hovered or holds the focus, and while the menu it opens stays open. */
export const PanelListItemActionsTrigger = forwardRef<HTMLButtonElement, FloatingTriggerButtonProps>(
	({ className, ...otherProps }, ref) => (
		<FloatingTriggerButton
			className={cn(
				"opacity-0 transition-opacity focus-visible:opacity-100 group-hover/panel-item:opacity-100 group-focus-within/panel-item:opacity-100 data-[state=open]:opacity-100",
				className,
			)}
			ref={ref}
			{...otherProps}
		>
			<Icon icon="ellipsis" />
		</FloatingTriggerButton>
	),
);

PanelListItemActionsTrigger.displayName = "PanelListItemActionsTrigger";
